import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import { observer } from 'mobx-react-lite'
import CenterFullScreen from 'reusable/mui/utilities/CenterFullScreen'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'
import { useAuthStore } from 'auth/AuthStore'
import { useEffect } from 'react'
import useStore from 'reusable/mobx-store-utils/useStore'
import { LoadingModal } from 'reusable/mui/modals/LoadingModal'
import { themeConfig } from '@gap/shared/theme'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import SignUpAdvisorSelectOrgStore, { SignUpAdvisorSelectOrgStoreProvider, useSignUpAdvisorSelectOrgStore } from './SignUpAdvisorSelectOrgStore'
import { UserOrgWithOrgDto } from '@gap/backend/app-services/user-org/models/UserOrgWithOrgDto'
import RadioButtonCheckedOutlined from '@mui/icons-material/RadioButtonCheckedOutlined'
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined'
import IconButton from '@mui/material/IconButton'
import ArrowRightAltOutlined from '@mui/icons-material/ArrowRightAltOutlined'

export default observer(function SignUpAdvisorSelectOrg() {
   const authStore = useAuthStore()
   const signUpAdvisorSelectOrgStore = useStore(sp => new SignUpAdvisorSelectOrgStore(sp), { authStore })

   useEffect(() => {
      signUpAdvisorSelectOrgStore.ensureUserInfoIsLoaded()
   }, [signUpAdvisorSelectOrgStore])

   if (signUpAdvisorSelectOrgStore.loader.isLoading) {
      return <LoadingModal visible title={signUpAdvisorSelectOrgStore.loader.loaderText} />
   }

   return <SignUpAdvisorSelectOrgStoreProvider store={signUpAdvisorSelectOrgStore}>
      <CenterFullScreen>
         <AuthCardWrapper title={`Thanks, ${authStore.userInfo?.firstName}!`} subtitle="You are now logged in and can continue to your account.">
            <Typography variant="body1Bold">Choose where you'd like to go first</Typography>
            {signUpAdvisorSelectOrgStore.orgs.map(org => <AuthOrgCard org={org} key={org.orgId} />)}
            <FlexAlignRight sx={{ mt: 2 }}>
               {signUpAdvisorSelectOrgStore.canSetupNewTrialOrg && <Button sx={{ mr: 2 }} onClick={signUpAdvisorSelectOrgStore.setupNewTrialOrg}>Get set up as an advisor</Button>}
               <Button variant="contained" onClick={signUpAdvisorSelectOrgStore.navigateToSelectedOrg} endIcon={<ArrowRightAltOutlined />}>Continue to {signUpAdvisorSelectOrgStore.selectedOrg?.orgType} org</Button>
            </FlexAlignRight>
         </AuthCardWrapper>
      </CenterFullScreen>
   </SignUpAdvisorSelectOrgStoreProvider>
})

const AuthOrgCard = observer(function AuthOrgCard(props: { org: UserOrgWithOrgDto }) {
   const signUpAdvisorSelectOrgStore = useSignUpAdvisorSelectOrgStore()

   const AuthOrgCardStore = useStore(() => ({
      clickHandler: () => {
         signUpAdvisorSelectOrgStore.selectOrg(props.org)
      }
   }))

   return <Box>
      <Tooltip title={props.org.name} onClick={AuthOrgCardStore.clickHandler}>
         <Box sx={{ p: 2, mt: 1, border: '1px solid', borderColor: themeConfig.light.borderColour, borderRadius: '16px', cursor: 'pointer' }}>
            <Stack direction="row" spacing={2} alignItems="center">
               <IconButton>
                  {props.org.orgId === signUpAdvisorSelectOrgStore.selectedOrg?.orgId && <RadioButtonCheckedOutlined sx={{ color: themeConfig.light.action.active }} />}
                  {props.org.orgId !== signUpAdvisorSelectOrgStore.selectedOrg?.orgId && <RadioButtonUncheckedOutlined sx={{ color: themeConfig.light.action.active }} />}
               </IconButton>
               <Box>
                  <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
                     {props.org.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                     {props.org.orgType}
                  </Typography>
               </Box>
            </Stack>
         </Box>
      </Tooltip>
   </Box>
})